import type { SupabaseClient } from '@supabase/supabase-js';

export type ProgramRow = { id: string; name: string; description: string | null; weeks: number; is_active: boolean; updated_at: string };
export type ProgramExercise = { id?: string; name: string; sets: number; reps: number | null; weight: number | null; rest_s: number; notes?: string | null };
export type ProgramDay = { id: string; week: number; position: number; name: string; program_exercises: ProgramExercise[] };
export type ProgramDetail = ProgramRow & { program_days: ProgramDay[] };
export type NextDay = {
  program_id: string;
  program_name: string;
  day_id: string | null;
  day_week: number | null;
  day_position: number | null;
  day_name: string | null;
  completed_days: number;
  total_days: number;
};

/** Editable form state for one exercise row (strings while typing). */
export type ExerciseDraft = { key: string; name: string; sets: string; reps: string; weight: string; rest_s: string; notes: string };

export function toDraft(e: ProgramExercise, key: string): ExerciseDraft {
  return {
    key,
    name: e.name,
    sets: String(e.sets),
    reps: e.reps == null ? '' : String(e.reps),
    weight: e.weight == null ? '' : String(e.weight),
    rest_s: String(e.rest_s),
    notes: e.notes ?? '',
  };
}

export function emptyDraft(key: string): ExerciseDraft {
  return { key, name: '', sets: '3', reps: '8', weight: '', rest_s: '90', notes: '' };
}

const num = (s: string) => (s.trim() === '' ? null : Number(s.trim().replace(',', '.')));

/** Validates drafts; returns clean exercises or the first problem (with its row). */
export function parseDrafts(drafts: ExerciseDraft[]): { ok: true; exercises: ProgramExercise[] } | { ok: false; error: string; row: number } {
  const out: ProgramExercise[] = [];
  for (let i = 0; i < drafts.length; i++) {
    const d = drafts[i]!;
    const label = `Exercise ${i + 1}`;
    const name = d.name.trim();
    if (!name) return { ok: false, error: `${label}: enter a name`, row: i };
    if (name.length > 80) return { ok: false, error: `${label}: name is too long`, row: i };
    const sets = num(d.sets);
    if (sets == null || !Number.isInteger(sets) || sets < 1 || sets > 20) return { ok: false, error: `${label}: sets must be 1–20`, row: i };
    const reps = num(d.reps);
    if (reps != null && (!Number.isInteger(reps) || reps < 0 || reps > 1000)) return { ok: false, error: `${label}: reps must be a whole number`, row: i };
    const weight = num(d.weight);
    if (weight != null && (Number.isNaN(weight) || weight < 0 || weight > 2000)) return { ok: false, error: `${label}: weight must be 0–2000`, row: i };
    const rest = num(d.rest_s) ?? 90;
    if (!Number.isInteger(rest) || rest < 0 || rest > 3600) return { ok: false, error: `${label}: rest must be 0–3600 seconds`, row: i };
    out.push({ name, sets, reps, weight: weight == null ? null : Math.round(weight * 100) / 100, rest_s: rest, notes: d.notes.trim() || null });
  }
  return { ok: true, exercises: out };
}

export function move<T>(list: T[], from: number, to: number): T[] {
  if (to < 0 || to >= list.length) return list;
  const copy = list.slice();
  const [item] = copy.splice(from, 1);
  copy.splice(to, 0, item as T);
  return copy;
}

export function summarizeDay(d: ProgramDay, units: string): string {
  if (!d.program_exercises.length) return 'No exercises yet';
  return d.program_exercises
    .slice()
    .sort((a: any, b: any) => (a.position ?? 0) - (b.position ?? 0))
    .map((e) => `${e.name} ${e.sets}×${e.reps ?? '–'}${e.weight != null ? ` @ ${e.weight}${units}` : ''}`)
    .join(' · ');
}

// ───── API ─────

export async function listPrograms(c: SupabaseClient): Promise<ProgramRow[]> {
  const { data, error } = await c.from('programs').select('id,name,description,weeks,is_active,updated_at').is('archived_at', null).order('updated_at', { ascending: false });
  if (error) throw error;
  return data as ProgramRow[];
}

export async function getProgram(c: SupabaseClient, id: string): Promise<ProgramDetail> {
  const { data, error } = await c
    .from('programs')
    .select('id,name,description,weeks,is_active,updated_at, program_days(id,week,position,name, program_exercises(id,position,name,sets,reps,weight,rest_s,notes))')
    .eq('id', id)
    .single();
  if (error) throw error;
  const p = data as unknown as ProgramDetail;
  p.program_days.sort((a, b) => a.week - b.week || a.position - b.position);
  for (const d of p.program_days) d.program_exercises.sort((a: any, b: any) => a.position - b.position);
  return p;
}

export async function createProgram(c: SupabaseClient, name: string, weeks: number, dayNames: string[]): Promise<ProgramRow> {
  const { data, error } = await c.rpc('create_program', { p_name: name, p_weeks: weeks, p_day_names: dayNames });
  if (error) throw error;
  return data as ProgramRow;
}

export async function saveDay(c: SupabaseClient, dayId: string, name: string, exercises: ProgramExercise[]) {
  const { error } = await c.rpc('save_program_day', { p_day_id: dayId, p_name: name, p_exercises: exercises });
  if (error) throw error;
}

export async function copyWeek(c: SupabaseClient, programId: string, from: number, to: number, weightDelta: number) {
  const { error } = await c.rpc('copy_program_week', { p_program_id: programId, p_from_week: from, p_to_week: to, p_weight_delta: weightDelta });
  if (error) throw error;
}

export async function setActive(c: SupabaseClient, programId: string) {
  const { error } = await c.rpc('set_active_program', { p_program_id: programId });
  if (error) throw error;
}

export async function archiveProgram(c: SupabaseClient, programId: string) {
  const { error } = await c.from('programs').update({ archived_at: new Date().toISOString(), is_active: false }).eq('id', programId);
  if (error) throw error;
}

export async function nextDay(c: SupabaseClient, programId?: string): Promise<NextDay | null> {
  const { data, error } = await c.rpc('next_program_day', programId ? { p_program_id: programId } : {});
  if (error) throw error;
  return (data as NextDay[])[0] ?? null;
}

// ───── Program structure (web builder; migration 20261017000100) ─────

async function call<T = unknown>(c: SupabaseClient, fn: string, args: Record<string, unknown>): Promise<T> {
  const { data, error } = await c.rpc(fn, args);
  if (error) throw error;
  return data as T;
}

export const duplicateProgram = (c: SupabaseClient, programId: string, name?: string) =>
  call<ProgramRow>(c, 'duplicate_program', { p_program_id: programId, p_name: name ?? null });
export const addProgramWeek = (c: SupabaseClient, programId: string, weightDelta = 0, repsDelta = 0) =>
  call<number>(c, 'add_program_week', { p_program_id: programId, p_weight_delta: weightDelta, p_reps_delta: repsDelta });
export const removeLastProgramWeek = (c: SupabaseClient, programId: string) => call<number>(c, 'remove_last_program_week', { p_program_id: programId });
export const addProgramDay = (c: SupabaseClient, programId: string, name: string) => call<number>(c, 'add_program_day', { p_program_id: programId, p_name: name });
export const removeProgramDay = (c: SupabaseClient, programId: string, position: number) =>
  call(c, 'remove_program_day', { p_program_id: programId, p_position: position });
export const renameProgramDay = (c: SupabaseClient, programId: string, position: number, name: string) =>
  call(c, 'rename_program_day', { p_program_id: programId, p_position: position, p_name: name });

export async function copyWeekWithReps(c: SupabaseClient, programId: string, from: number, to: number, weightDelta: number, repsDelta: number) {
  await call(c, 'copy_program_week', { p_program_id: programId, p_from_week: from, p_to_week: to, p_weight_delta: weightDelta, p_reps_delta: repsDelta });
}

export async function updateProgramMeta(c: SupabaseClient, programId: string, patch: { name?: string; description?: string | null }) {
  const { error } = await c.from('programs').update({ ...patch, updated_at: new Date().toISOString() }).eq('id', programId);
  if (error) throw error;
}
